export const GPS_ODOMETER_POLICY_VERSION = 'GPS_ODOMETER_V1';

export interface GpsOdometerFix {
  lat: number;
  lng: number;
  accuracyM: number | null;
  speedKmh?: number | null;
}

export interface GpsOdometerEvaluation {
  displacementM: number;
  thresholdM: number;
  movementAccepted: boolean;
  deltaKm: number;
}

const normalizedAccuracy = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 15;
};

export function gpsOdometerDistanceMeters(
  a: Pick<GpsOdometerFix, 'lat' | 'lng'>,
  b: Pick<GpsOdometerFix, 'lat' | 'lng'>
): number {
  const R = 6371000;
  const rad = (d: number) => d * Math.PI / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const q =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) *
      Math.cos(rad(b.lat)) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(q), Math.sqrt(1 - q));
}

export function gpsOdometerGateMeters(
  previousAccuracyM: number | null,
  currentAccuracyM: number | null,
  speedKmh: number | null | undefined
): number {
  const uncertainty = Math.max(
    normalizedAccuracy(previousAccuracyM),
    normalizedAccuracy(currentAccuracyM)
  );
  const moving =
    Number.isFinite(Number(speedKmh)) &&
    Number(speedKmh) >= 2;

  return moving
    ? Math.max(5, Math.min(15, uncertainty * 0.45))
    : Math.max(8, Math.min(30, uncertainty * 0.90));
}

export function evaluateGpsOdometerMovement(
  previous: GpsOdometerFix,
  current: GpsOdometerFix
): GpsOdometerEvaluation {
  const displacementM = gpsOdometerDistanceMeters(previous, current);
  const thresholdM = gpsOdometerGateMeters(
    previous.accuracyM,
    current.accuracyM,
    current.speedKmh
  );
  const movementAccepted = displacementM >= thresholdM;

  return {
    displacementM,
    thresholdM,
    movementAccepted,
    deltaKm: movementAccepted ? displacementM / 1000 : 0,
  };
}
